import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BreadcrumbStyles } from './Breadcrumb.styled';
import VectorEllipses from '../Icons/VectorEllipses';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';

export interface Crumb {
  text: string;
  url?: string;
}

interface BreadcrumbProps {
  breadcrumbs: Crumb[];
  className?: string;
  style?: React.CSSProperties;
  useOverflowMenu?: boolean;
}

const Breadcrumb = React.forwardRef<HTMLDivElement, BreadcrumbProps>(
  ({ breadcrumbs, className, style, useOverflowMenu = false }, ref) => {
    const crumbs = breadcrumbs;

    const [activeDropdown, setActiveDropdown] = useState<boolean>(false);

    const toggleDropdown = () => {
      setActiveDropdown(prev => !prev);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        setActiveDropdown(prev => !prev);
      }
    };

    const crumbDropdownRef = useRef<HTMLDivElement>(null);

    const handleOutsideClicks = (e: MouseEvent) => {
      if (activeDropdown) {
        const dropdownArea = crumbDropdownRef.current;

        // Check if click was outside both areas
        const clickedOutsideDropdown =
          !dropdownArea ||
          (e.target !== dropdownArea &&
            !dropdownArea.contains(e.target as Node));

        // If click was outside area, close the dropdown
        if (clickedOutsideDropdown) {
          setActiveDropdown(false);
        }
      }
    };

    useOnClickOutside(crumbDropdownRef, handleOutsideClicks);

    useEffect(() => {
      /**
       * Closes the dropdown when the user scrolls.
       */
      const handleScroll = () => {
        setActiveDropdown(false);
      };

      document.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        document.removeEventListener('scroll', handleScroll);
      };
    }, []);

    return (
      <BreadcrumbStyles
        aria-label='breadcrumb'
        className={className || ''}
        ref={ref}
        style={style}
        $showLeadingSlash={useOverflowMenu}
        $hasBreadcrumbs={crumbs?.length > 0}
      >
        <div className='breadcrumb-container'>
          {crumbs.length > 0 && useOverflowMenu && (
            <div
              className={`ellipsis-icon ${activeDropdown ? 'active' : ''}`}
              onClick={() => toggleDropdown()}
              onKeyDown={e => {
                handleKeyPress(e);
              }}
              tabIndex={0}
            >
              <VectorEllipses />
              <div
                className={`bc-dropdown ${activeDropdown ? 'active' : ''}`}
                ref={crumbDropdownRef}
              >
                {crumbs?.map((crumb, i) => {
                  const isLast = i === crumbs.length - 1;
                  return (
                    <div
                      key={`bc-dropdown-item-${i}`}
                      className='bc-dropdown-item'
                    >
                      {!isLast && crumb?.url ? (
                        <Link to={crumb.url}>{crumb.text}</Link>
                      ) : (
                        crumb.text
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <ol className='breadcrumb'>
            {(useOverflowMenu
              ? crumbs?.length
                ? crumbs.slice(-2)
                : [] // Get only the last two items
              : crumbs || []
            ).map((crumb, i, displayedCrumbs) => {
              // Use displayedCrumbs for accurate isLast check
              const isLast = i === displayedCrumbs.length - 1;
              return (
                <li
                  key={`breadcrumb-item-${i + 1}`}
                  className='breadcrumb-item'
                  style={
                    isLast
                      ? {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          maxWidth: '100%',
                        }
                      : undefined
                  }
                >
                  {!isLast && crumb?.url ? (
                    <Link to={crumb.url}>{crumb.text}</Link>
                  ) : (
                    crumb.text
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </BreadcrumbStyles>
    );
  }
);

export default Breadcrumb;
